<template>
  <Carousel
    v-if="isTableFilled"
    :per-page-custom="[
      [1, 1],
      [640, 2],
      [1024, 3],
      [1500, 4],
    ]"
    pagination-color="#ededed"
    pagination-active-color="#e20074"
  >
    <Slide v-for="(contract, key) in contractsComputed" :key="key" v-slot="{ isActive }" :title="$tc('common.resultPage', key + 1)">
      <div class="contract" :class="{ selected: isContractSelected(contract) }" :aria-selected="String(isContractSelected(contract))">
        <div class="contract-header">
          <img class="contract-icon" :src="crmIcon(contract.crm)" :alt="$t(`common.crm_${contract.crm}`)" />
          <h5 :id="idFor(key)" style="margin-top: 0">{{ contract.phone || contract.tariffName }}</h5>
          <p v-if="contract.phone">{{ contract.tariffName }}</p>
        </div>
        <div class="contract-info">
          <div v-if="contract.accessMode === 'ro'" class="move-children-bottom">
            <p>
              {{ $t('common.contract_read_only_note') }}
              <a v-if="externalContractManagementUrl" :href="externalContractManagementUrl">{{ $t('common.change') }}</a>
            </p>
          </div>
          <VDescriptionList v-else>
            <VEntry v-slot="{ value }" :data="contract" field="status" name="common.contractStatus">{{ mapContractState(value) }}</VEntry>
            <VEntry v-if="contract.isFixedLine() && hasShowTechnicalInformation" :data="contract" field="identifierInfo.NIK0.value" name="common.contractId" />
            <VEntry v-if="contract.isFixedLine() && hasShowTechnicalInformation" :data="contract" field="groupKey" name="common.groupKey" />
            <VEntry v-if="contract.isMobile() && hasShowTechnicalInformation" :data="contract" field="identifierInfo.IK.value" name="common.contractNumber" />
          </VDescriptionList>
        </div>
        <div class="contract-button-bar">
          <VButton
            class="btn-default"
            label="common.btnSelect"
            :selected="isContractSelected(contract)"
            :disabled="!isActive"
            :aria-describedby="idFor(key)"
            @click="onSelectContract(contract)"
          />
        </div>
      </div>
    </Slide>
  </Carousel>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { Carousel, Slide } from '@/components/UI/Carousel';
import VDescriptionList from '@/components/DescriptionList';
import VEntry from '@/components/DescriptionList/Entry';
import VButton from '@/components/Button';
import { mapContractState } from '@/utils/i18n';

export default {
  name: 'ContractSelection',

  components: {
    Carousel,
    Slide,
    VDescriptionList,
    VEntry,
    VButton,
  },

  props: {
    contracts: {
      type: Array,
      required: true,
    },

    to: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      id: Math.random().toString(36).substring(2),
    };
  },

  computed: {
    ...mapState('system', ['externalContractManagementUrl']),

    ...mapState('contract', ['identifierInfo']),

    ...mapGetters('system', ['hasShowTechnicalInformation']),

    isTableFilled() {
      return !!this.contracts.length;
    },

    contractsComputed() {
      return this.contracts.map((contract) =>
        Object.freeze({
          ...contract,
          isFixedLine: () => contract.crm === 'FixedLine',
          isMobile: () => contract.crm === 'Mobile',
          isCable: () => contract.crm === 'Cable',
          isUnified: () => contract.crm === 'Unified',
        })
      );
    },
  },

  methods: {
    mapContractState,

    // Resolve crm icon safely: a missing svg would otherwise throw during render.
    crmIcon(crm) {
      try {
        return require(`@/assets/images/${crm}.svg`);
      } catch (err) {
        return '';
      }
    },

    onSelectContract(contract) {
      const values = {
        identifierName: contract.identifierInfo.NIK0.name,
        identifierValue: contract.identifierInfo.NIK0.value,
      };
      this.$emit('select', { values, routeTo: this.to, routeToParams: contract.routeToParams });
    },

    isContractSelected(contract) {
      return contract.identifierInfo.NIK0.value === this.identifierInfo?.NIK0.value;
    },

    idFor(key) {
      return this.id + '_' + key;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

::v-deep .carousel-slide {
  margin-bottom: 0.5px; /* HACK to work around a Firefox rendering issue (see TSPC-2193) */
}

.contract {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 320px;
  height: 100%;
  margin: auto;
  border: 1px solid $color-border-bright;
  border-radius: 4px;
}

.contract-header {
  height: 90px;
  padding: 12px;
}

.contract-info {
  flex-grow: 1;
  padding: 12px;
}

.contract-button-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  border-top: 1px solid $color-border-bright;
}

.contract-icon {
  float: left;
  height: 100%;
  margin-right: 12px;
}

.dl1 dd {
  /* hide horizontal lines between list entries */
  border-bottom: none !important;
}

.selected {
  border-color: $color-brand;
}

.move-children-bottom {
  display: flex;
  flex-direction: column-reverse;
  height: 100%;
}

p {
  padding: 0;
}
</style>
